// ============================================================================
// src/lib/pigOperationsApi.ts — the one client of public.pig_apply_operation
// ----------------------------------------------------------------------------
// Migrations 231 (B4a: batches, sub-batches, mortality) and 235 (B4b: breeding
// pigs, breeding cycles, farrowing records) replace whole-document app_store
// writes with ONE audited server entrypoint. It applies a narrow change to one
// record under the document's row lock, after checking the caller's
// expected-before values, and writes the Activity row and an immutable request
// receipt in the same transaction. The contract this module implements is
// wcf-receipts\cc1-b4-pig-writers\CLIENT-CONTRACT-231-v2.md.
//
// WHAT THIS MODULE OWNS: the transport and the meaning of every reply.
//
//   * REQUEST IDS. One per user action (`newPigRequestId`). A retry of the same
//     action reuses it: the server either replays the stored result or applies
//     the operation once. A new id is only ever taken after a CONFIRMED outcome.
//   * RETRIES. A transport failure (no reply, timeout) is retried here with the
//     SAME id — safe because of the receipt. A database refusal (any SQLSTATE)
//     is never retried: it is an answer, not a failure.
//   * REPLAYS. A replayed result carries no rows (the receipt deliberately holds
//     no document copy). `mustReread` tells the caller to refetch before it
//     adopts anything; so does `unknown`.
//   * EXPECTED VALUES. `expectedFrom` reads them off the PERSISTED row, type-
//     exact, never off a form: legacy batches store counts as strings, and the
//     server compares JSON exactly.
//
// WHAT IT DOES NOT OWN: what the UI shows. Callers map outcomes to notices.
// ============================================================================

import type {SupabaseClient} from './supabaseTypes.js';
import {newClientSubmissionId} from './clientSubmissionId.js';

export type PigOperation =
  | 'feeder.create'
  | 'feeder.update'
  | 'feeder.archive'
  | 'feeder.reopen'
  | 'feeder.delete'
  | 'sub_batch.create'
  | 'sub_batch.update'
  | 'sub_batch.partition'
  | 'sub_batch.archive'
  | 'sub_batch.reopen'
  | 'sub_batch.delete'
  | 'mortality.append'
  | 'mortality.delete'
  | 'breeder.create'
  | 'breeder.update'
  | 'breeder.delete'
  | 'breeding.create'
  | 'breeding.update'
  | 'breeding.delete'
  | 'farrowing.create'
  | 'farrowing.update'
  | 'farrowing.delete';

export type PigOpTarget = {
  feeder_id?: string;
  sub_batch_id?: string;
  mortality_id?: string;
  breeder_id?: string;
  cycle_id?: string;
  farrowing_id?: string;
};

type Json = unknown;
export type PigAbsent = {readonly __absent: true};
/** The server's spelling of "this property is missing" (distinct from null and ""). */
export const PIG_ABSENT: PigAbsent = Object.freeze({__absent: true as const});

export type PigConflict = {field: string; expected: Json; current: Json};

export type PigOpRequest = {
  requestId: string;
  operation: PigOperation;
  target: PigOpTarget;
  expected?: Record<string, Json>;
  change?: Record<string, Json>;
};

export type PigOpOutcome =
  | {
      status: 'saved';
      kind: 'created' | 'updated' | 'deleted';
      written: Record<string, Json>;
      revision: string | null;
      eventIds: string[];
      rows: Record<string, Json> | null;
      replayed: boolean;
      mustReread: boolean;
    }
  | {status: 'no_change'; rows: Record<string, Json> | null}
  | {status: 'conflict'; conflicts: PigConflict[]; rows: Record<string, Json> | null}
  | {status: 'refused'; reason: string; links: Record<string, string[]>; rows: Record<string, Json> | null}
  | {status: 'rejected'; code: string; message: string}
  | {status: 'unknown'; message: string; mustReread: true};

/** A fresh id for ONE user action. Keep it with the draft and reuse it on retry. */
export function newPigRequestId(): string {
  return 'pig-' + newClientSubmissionId();
}

/**
 * The expected-before values for `fields`, read off the persisted `row` exactly
 * as stored. A field the row does not have is PIG_ABSENT.
 */
export function expectedFrom(row: Record<string, Json> | null | undefined, fields: readonly string[]) {
  const out: Record<string, Json> = {};
  for (const field of fields) {
    out[field] = row && Object.prototype.hasOwnProperty.call(row, field) ? row[field] : PIG_ABSENT;
  }
  return out;
}

const ATTEMPTS = 3;
const ATTEMPT_TIMEOUT_MS = 8000;
// A five-character SQLSTATE (22023, P0002, WCF01, ...) is the database's answer.
const SQLSTATE = /^[0-9A-Z]{5}$/;

type RpcError = {code?: string; message?: string} | null;

function rowsOf(data: Record<string, Json>): Record<string, Json> | null {
  const rows = data.rows;
  return rows && typeof rows === 'object' && !Array.isArray(rows) ? (rows as Record<string, Json>) : null;
}

/** Read one reply. Anything that does not answer THIS request is not a receipt. */
export function interpretPigReply(request: PigOpRequest, data: unknown): PigOpOutcome {
  const d = (data && typeof data === 'object' ? data : {}) as Record<string, Json>;
  if (d.request_id !== request.requestId || d.operation !== request.operation) {
    return {status: 'unknown', message: 'Could not confirm this change was saved. Reload to check.', mustReread: true};
  }
  const rows = rowsOf(d);
  switch (d.kind) {
    case 'created':
    case 'updated':
    case 'deleted': {
      if (d.ok !== true) break;
      const replayed = d.replayed === true;
      return {
        status: 'saved',
        kind: d.kind,
        written: (d.written && typeof d.written === 'object' ? d.written : {}) as Record<string, Json>,
        revision: typeof d.revision === 'string' ? d.revision : null,
        eventIds: Array.isArray(d.event_ids) ? (d.event_ids as string[]) : [],
        rows: replayed ? null : rows,
        replayed,
        mustReread: replayed || rows === null,
      };
    }
    case 'no_change':
      return {status: 'no_change', rows};
    case 'conflict':
      return {status: 'conflict', conflicts: Array.isArray(d.conflicts) ? (d.conflicts as PigConflict[]) : [], rows};
    case 'refused':
      return {
        status: 'refused',
        reason: typeof d.reason === 'string' ? d.reason : 'refused',
        links: (d.links && typeof d.links === 'object' ? d.links : {}) as Record<string, string[]>,
        rows,
      };
  }
  return {status: 'unknown', message: 'Could not confirm this change was saved. Reload to check.', mustReread: true};
}

/**
 * Apply one pig operation. Never throws. Transport failures are retried with the
 * same request id; a database refusal comes back as `rejected` at once.
 */
export async function applyPigOperation(sb: SupabaseClient, request: PigOpRequest): Promise<PigOpOutcome> {
  let lastMessage = 'No reply from the server.';
  for (let attempt = 1; attempt <= ATTEMPTS; attempt++) {
    let timer: ReturnType<typeof setTimeout> | null = null;
    try {
      const call = sb.rpc('pig_apply_operation', {
        p_request_id: request.requestId,
        p_operation: request.operation,
        p_target: request.target,
        p_expected: request.expected ?? {},
        p_change: request.change ?? {},
      });
      const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('timeout')), ATTEMPT_TIMEOUT_MS);
      });
      const {data, error} = (await Promise.race([call, timeout])) as {data: unknown; error: RpcError};
      if (error) {
        if (error.code && SQLSTATE.test(error.code)) {
          return {status: 'rejected', code: error.code, message: error.message || 'The change was refused.'};
        }
        throw new Error(error.message || 'transport');
      }
      return interpretPigReply(request, data);
    } catch (e) {
      lastMessage = (e as {message?: string}).message || lastMessage;
      if (attempt < ATTEMPTS) await new Promise((r) => setTimeout(r, 1000 * attempt));
    } finally {
      if (timer !== null) clearTimeout(timer);
    }
  }
  console.warn('pig_apply_operation: no confirmed reply after retries:', lastMessage);
  return {status: 'unknown', message: 'Could not reach the server. Your change is kept; retry.', mustReread: true};
}

/** The user-facing sentence for a refusal, by SQLSTATE (contract §3). */
export function pigRejectionMessage(outcome: Extract<PigOpOutcome, {status: 'rejected'}>): string {
  switch (outcome.code) {
    case '22023':
      return outcome.message.replace(/^pig_apply_operation: /, '');
    case '23505':
      return 'This was already saved, or another record has the same identity. Reload to check.';
    case 'P0002':
      return 'This record was removed elsewhere. Reload to see the current list.';
    case '42501':
      return 'Your session or role changed. Sign in again; your change is kept.';
    case 'WCF01':
      return 'This change no longer matches what was saved. Reload and try again.';
    default:
      return outcome.message || 'The change was refused.';
  }
}
